import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/Header';
import { SEO } from '@/components/SEO';
import { Footer } from '@/components/Footer';

/** One legal page (legal notice, privacy policy, terms): title, update date,
 *  intro, the sections of `${ns}.sections` (title, paragraphs, bullet items),
 *  then the contact section. `children` are shown before the sections. */
export const LegalSection = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-semibold text-primary mb-3">{title}</h2>
    <div className="text-slate-600 leading-relaxed text-sm sm:text-base space-y-2 break-words">
      {children}
    </div>
  </section>
);

export function LegalLayout({ ns, seo, testId, children }) {
  const { t } = useTranslation();
  const sections = t(`${ns}.sections`, { returnObjects: true });
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO title={t(`seo.${seo}.title`)} description={t(`seo.${seo}.description`)} />
      <Header />
      <main className="flex-1 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid={testId}>
          <h1 className="text-3xl sm:text-5xl font-bold text-primary tracking-tight">{t(`${ns}.pageTitle`)}</h1>
          <p className="mt-3 text-xs text-slate-400" data-testid={`${testId}-updated`}>{t(`${ns}.updated`)}</p>
          <p className="mt-6 text-sm text-slate-500">{t(`${ns}.intro`)}</p>
          {children}
          {(Array.isArray(sections) ? sections : []).map((s) => (
            <LegalSection key={s.title} title={s.title}>
              {(s.body || []).map((p) => <p key={p}>{p}</p>)}
              {s.items?.length > 0 && (
                <ul className="list-disc pl-5 space-y-1">
                  {s.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </LegalSection>
          ))}
          <LegalSection title={t(`${ns}.contactTitle`)}>
            <p>
              {t(`${ns}.contactTextBefore`)}
              <Link to="/contact" className="text-accent hover:underline" data-testid={`${ns}-contact-link`}>
                {t(`${ns}.contactLinkText`)}
              </Link>
              {t(`${ns}.contactTextAfter`)}
            </p>
          </LegalSection>
        </div>
      </main>
      <Footer />
    </div>
  );
}
